<template>
  <div class="Page">
    <div class="Inner">
      <h1 class="PageTitle">購入</h1>
      <Steps
        :total-steps="3"
        :labels="['入力', '確認', '完了']"
        :current-step="2"
      />
    </div>
    <Modal :show="showModal" @close="showModal = false">
      <template #body>
        <div class="LoadingModal">
          <div class="LoadingWrapper">
            <Loading/>
          </div>
          <div class="LoadingMessage">
            現在、決済処理中です。自動で画面が切り替わるまで1〜5秒ほどお待ちください。
          </div>
          <div class="LoadingSubMessage">
            ※しばらく経っても画面が切り替わらない場合は、<TextLink :to="contactLink" text="お問合せフォーム" target="_blank"/>からお問合せください。
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
import {
  defineNuxtComponent,
  useRequestHeaders,
  navigateTo,
  createError,
} from "#app";
import { ref } from 'vue'
import { definePageMeta, useSeoMeta, useRuntimeConfig, useHead } from "#imports";
import type { CartStore } from "@/store/cart";
import type { ToastStore } from "@/store/toast";
import type { ProfileStore } from '@/store/profile'
import type {
  BodyType,
  ResponseType,
} from "@/server/api/order/continue/index.post";
import { isH3Error } from "@/utils/error";
import { useProfileWeb } from '@/composables/useProfileWeb'
import { Steps, Loading, Modal, TextLink } from '@ka-nabellinc/ka-nabell-ui'

export default defineNuxtComponent({
  components: {
    Steps,
    Loading,
    Modal,
    TextLink
  },
  setup() {
    const showModal = ref(true) // 不具合の発生時にModalを閉じられるようにrefを用意
    const { contactLink } = useProfileWeb()
    const { public: { APP_ENV } } = useRuntimeConfig()

    const title = '決済待ち - カーナベル'
    useSeoMeta({
      title,
      ogTitle: title,
      robots: APP_ENV==='production' ? 'noindex,follow' :'noindex,nofollow',
    })


    definePageMeta({
      genre: 'pokemon',
      async middleware({ query: { AccessID } }) {
        if (process.server) return;
        try {
          const pinia = usePinia() as any;
          // Note: reflect-metadataが利用できないのでusePiniaで直接参照
          const toastStore = pinia._s.get("toast") as ToastStore;
          const cartStore = pinia._s.get("cart") as CartStore;
          const profileStore = pinia._s.get("profile") as ProfileStore

          // GMOからのredirectを処理
          if (typeof AccessID === "string" && !!AccessID) {
            const body: BodyType = {
              accessId: AccessID,
            };
            const headers = useRequestHeaders();
            try {
              const { mySellingOrder } = await $fetch<ResponseType>(
                "/api/order/continue",
                {
                  method: "POST",
                  body,
                  headers,
                }
              );

              try {

                // GTM Event https://docs.google.com/spreadsheets/d/1ZLvgxU1CnwcEaohiMpFKnL2A8C75_4j5I1Ol7Tzb2fU/edit?gid=906310070#gid=906310070&range=B23
                if (typeof window !== 'undefined') {
                  const genre = 'pokemon'
                  const cartItems = genre === "pokemon" ? cartStore.pokemonCart.items : [];
                  let items: GTMSingleItemType[] = []
                  let transactionProducts: GTMTransactionProductType[] = []

                  for (const item of cartItems) {
                    transactionProducts.push({
                      inventory_id: item.inventoryId,
                      name: item.product.product_name,
                      category: genre === 'pokemon' ? 'ポケモンカード' : 'ポケモンカード',
                      price: item.unitPrice,
                      quantity: item.orderNum
                    })

                    items.push({
                      item_id: item.inventoryId,
                      item_name: item.product.product_name,
                      item_category: genre === 'pokemon' ? 'ポケモンカード' : 'ポケモンカード',
                      price: item.unitPrice,
                      quantity: item.orderNum
                    })
                  }

                  window.dataLayer?.push({
                    event: 'sell',
                    selling_order_id: mySellingOrder.sellingOrderId,
                    transaction_total: mySellingOrder.billingAmount,
                    transaction_products: transactionProducts,
                    ecommerce: {
                      transaction_id: mySellingOrder.sellingOrderId,
                      currency: 'JPY',
                      value: mySellingOrder.billingAmount,
                      items
                    },
                    user_id: profileStore.uid
                  })
                }

              } catch (e) {
                // Do nothing
                console.log(e)
              }

              // カートの中身を消去
              cartStore.clearPokemonCart();

              return navigateTo({
                path: "/pokemon/order/complete",
                query: {
                  sellingOrderId: mySellingOrder.sellingOrderId,
                },
              });
            } catch (e) {
              console.log(e)
              let message = "決済に失敗しました";

              if (isH3Error(e) && e.data?.message) {
                message = e.data.message;
              }

              toastStore.setToast({
                mode: "error",
                message,
              });

              return navigateTo("/pokemon/order");
            }
          }

          throw createError({
            statusCode: 503,
            message: "Something went wrong",
          });
        } catch (e) {
          throw e;
        }
      },
      layout: 'simple'
    });

    return {
      showModal,
      contactLink
    }
  },
});
</script>

<style scoped>
.Page {
  width: 100%;
  padding: 20px 16px 48px;
  min-height: 456px;
}

.Inner {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
}

.PageTitle {
  width: 100%;
}

.LoadingModal {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
}

.LoadingWrapper {
  width: 88px;
  height: 88px;
}

.LoadingMessage {
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.LoadingSubMessage {
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0.12px;
}
</style>
